import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Screen } from "@/components/layout/Screen";
import { Card } from "@/components/ui/card";
import { CurrencyIcon } from "@/components/layout/CountryCurrency";

import {
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Shield,
  Calendar,
  Smartphone,
  Handshake,
  Camera,
  ChevronRight,
  ChevronDown,
  Utensils,
  Activity,
  Users,
  FileCheck,
  Skull,
  ClipboardCheck,
  Bird,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useCBSScoreStore } from "@/stores/cbsScoreStore";
import { useCBSBatchStore } from "@/stores/cbsBatchStore";
import { CBS_CONSTANTS } from "@/types/cbs";
import { getAuthenticatedClient, getCurrentUserId } from "@/services/client";
import { edgeFunctions } from "@/services/edgeFunctions";
import { useToast } from "@/hooks/use-toast";

// Simple Mode hook with fallback
let useSimpleMode: () => { isSimpleMode: boolean };
try {
  useSimpleMode = require("@/contexts/SimpleModeContext").useSimpleMode;
} catch {
  useSimpleMode = () => ({ isSimpleMode: false });
}

// Mood emoji based on score percentage
const getMoodEmoji = (score: number): string => {
  const percent = ((score - CBS_CONSTANTS.MIN_CREDIT_SCORE) / (CBS_CONSTANTS.MAX_CREDIT_SCORE - CBS_CONSTANTS.MIN_CREDIT_SCORE)) * 100;
  if (percent >= 70) return "😊";
  if (percent >= 50) return "😐";
  return "😟";
};

const getGaugeColor = (score: number): string => {
  const percent = ((score - CBS_CONSTANTS.MIN_CREDIT_SCORE) / (CBS_CONSTANTS.MAX_CREDIT_SCORE - CBS_CONSTANTS.MIN_CREDIT_SCORE)) * 100;
  if (percent >= 70) return "hsl(var(--success))";
  if (percent >= 50) return "hsl(var(--warning))";
  return "hsl(var(--destructive))";
};

type TrendDir = "up" | "stable" | "down";

interface PoultryFactor {
  id: string;
  label: string;
  labelKey: string;
  weight: number;
  scoreKey: string;
  icon: React.ElementType;
  quickWin: { route: string; action: string; actionKey: string; points: number };
}

// labelKey + quickWin.actionKey reuse the existing en/lo keys at score.factors.*
// and score.quickWins.* (verified PRESENT in both locales pre-Tier-1.5).
// Keeping the literal `label` and `action` fields as defaultValue fallbacks
// in case any factor's translation key is ever removed from the locale files.
const poultryFactors: PoultryFactor[] = [
  { id: "activity_logging", label: "Activity Logging", labelKey: "score.factors.activityLogging", weight: 15, scoreKey: "activity_logging_score", icon: Calendar, quickWin: { route: "/daily-record", action: "Log today's record", actionKey: "score.quickWins.logRecord", points: 10 } },
  { id: "photo_verification", label: "Photo Consistency", labelKey: "score.factors.photoConsistency", weight: 15, scoreKey: "photo_consistency_score", icon: Camera, quickWin: { route: "/camera", action: "Take a shed photo", actionKey: "score.quickWins.shedPhoto", points: 5 } },
  { id: "financial_behavior", label: "Settlement Performance", labelKey: "score.factors.settlementPerformance", weight: 10, scoreKey: "settlement_performance_score", icon: Activity, quickWin: { route: "/money", action: "View financial summary", actionKey: "score.quickWins.viewFinancial", points: 3 } },
  { id: "community_engagement", label: "Network Engagement", labelKey: "score.factors.networkEngagement", weight: 10, scoreKey: "network_engagement_score", icon: Users, quickWin: { route: "/network", action: "Answer a peer question", actionKey: "score.quickWins.answerQuestion", points: 8 } },
  { id: "platform_adoption", label: "Engagement Quality", labelKey: "score.factors.engagementQuality", weight: 5, scoreKey: "engagement_quality_score", icon: Smartphone, quickWin: { route: "/agent", action: "Use voice command", actionKey: "score.quickWins.useVoice", points: 5 } },
  { id: "fcr_performance", label: "FCR Performance", labelKey: "score.factors.fcrPerformance", weight: 15, scoreKey: "fcr_performance_score", icon: TrendingUp, quickWin: { route: "/log/feed", action: "Log feed data", actionKey: "score.quickWins.logFeed", points: 10 } },
  { id: "mortality_management", label: "Mortality Performance", labelKey: "score.factors.mortalityPerformance", weight: 10, scoreKey: "mortality_performance_score", icon: Skull, quickWin: { route: "/log/mortality", action: "Record mortality", actionKey: "score.quickWins.recordMortality", points: 5 } },
  { id: "biosecurity_compliance", label: "Biosecurity Compliance", labelKey: "score.factors.biosecurityCompliance", weight: 10, scoreKey: "biosecurity_compliance_score", icon: Shield, quickWin: { route: "/log/biosecurity", action: "Complete biosecurity check", actionKey: "score.quickWins.biosecurityCheck", points: 15 } },
  { id: "settlement_verification", label: "Verification Compliance", labelKey: "score.factors.verificationCompliance", weight: 5, scoreKey: "verification_compliance_score", icon: FileCheck, quickWin: { route: "/camera?category=document", action: "Upload settlement slip", actionKey: "score.quickWins.uploadSlip", points: 5 } },
  { id: "integrator_relationship", label: "Yield Accuracy", labelKey: "score.factors.yieldAccuracy", weight: 5, scoreKey: "yield_accuracy_score", icon: Handshake, quickWin: { route: "/network", action: "Share update", actionKey: "score.quickWins.shareUpdate", points: 3 } },
];

const factorBarColor = (score: number): string => {
  if (score >= 70) return "bg-success";
  if (score >= 40) return "bg-warning";
  return "bg-destructive";
};

const trendIcon = (dir: TrendDir) => {
  if (dir === "up") return <TrendingUp className="w-4 h-4 text-success" />;
  if (dir === "down") return <TrendingDown className="w-4 h-4 text-destructive" />;
  return <ArrowRight className="w-4 h-4 text-muted-foreground" />;
};

// Derive trend from score value (simplified heuristic)
const deriveTrend = (score: number): TrendDir => {
  if (score >= 65) return "up";
  if (score >= 35) return "stable";
  return "down";
};

export const ScoreScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isSimpleMode } = useSimpleMode();
  const [activeTab, setActiveTab] = useState("score");
  const [displayScore, setDisplayScore] = useState<number>(CBS_CONSTANTS.MIN_CREDIT_SCORE);
  const [expandedFactor, setExpandedFactor] = useState<string | null>(null);

  // CBS Store
  const { currentScore } = useCBSScoreStore();
  const creditScore = currentScore?.total_score ?? CBS_CONSTANTS.MIN_CREDIT_SCORE;
  const userId = getCurrentUserId();

  // Batch count for stat card
  const { data: batchCount = 0 } = useQuery({
    queryKey: ["cbs", "batch-count", userId],
    queryFn: async () => {
      if (!userId) return 0;
      const client = getAuthenticatedClient();
      const { count, error } = await client
        .from("batches")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .in("batch_status", ["completed", "abandoned"]);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!userId,
  });

  // Fetch full credit_scores row for factor scores
  const { data: scoreRow } = useQuery({
    queryKey: ["cbs", "credit-score-detail", userId],
    queryFn: async () => {
      if (!userId) return null;
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from("credit_scores")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });


  // Animated score counting
  useEffect(() => {
    const duration = 2000;
    const startTime = performance.now();
    const startValue = CBS_CONSTANTS.MIN_CREDIT_SCORE;
    const endValue = creditScore;
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setDisplayScore(Math.floor(startValue + (endValue - startValue) * easeOutCubic(progress)));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [creditScore]);


  // Gauge
  const gaugeSize = 200;
  const strokeWidth = 16;
  const radius = (gaugeSize - strokeWidth) / 2;
  const circumference = Math.PI * radius;
  const gaugeProgress = ((creditScore - CBS_CONSTANTS.MIN_CREDIT_SCORE) / (CBS_CONSTANTS.MAX_CREDIT_SCORE - CBS_CONSTANTS.MIN_CREDIT_SCORE)) * 100;
  const strokeDashoffset = circumference - (gaugeProgress / 100) * circumference;

  // Get factor score from the DB row
  const getFactorScore = (key: string): number => {
    if (!scoreRow) return 0;
    return (scoreRow as any)[key] ?? 0;
  };

  // Find lowest factor for action card.
  // Exclude factors whose quickWin.route is "/money" — that route is a passive
  // financial summary view, not an actionable score-improving step. The CTA
  // should always point at something the farmer can DO. Defensive fallback to
  // the unfiltered list if a future refactor empties actionableFactors.
  const actionableFactors = poultryFactors.filter((f) => f.quickWin.route !== "/money");
  const factorPool = actionableFactors.length > 0 ? actionableFactors : poultryFactors;
  const lowestFactor = factorPool.reduce((low, f) => {
    const s = getFactorScore(f.scoreKey);
    const ls = getFactorScore(low.scoreKey);
    return s < ls ? f : low;
  }, factorPool[0]);

  return (
    <Screen
      showTopBar
      showBottomNav
      topBarProps={{ title: t("cbs.score.organicCredit") }}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div>
        <div className="p-4 space-y-5">
          {/* Gauge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center py-6"
          >
            <div className="relative" style={{ width: gaugeSize, height: gaugeSize / 2 + 40 }}>
              <svg width={gaugeSize} height={gaugeSize / 2 + 20} className="overflow-visible">
                <path
                  d={`M ${strokeWidth / 2} ${gaugeSize / 2} A ${radius} ${radius} 0 0 1 ${gaugeSize - strokeWidth / 2} ${gaugeSize / 2}`}
                  fill="none"
                  stroke="hsl(var(--muted))"
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                />
                <motion.path
                  d={`M ${strokeWidth / 2} ${gaugeSize / 2} A ${radius} ${radius} 0 0 1 ${gaugeSize - strokeWidth / 2} ${gaugeSize / 2}`}
                  fill="none"
                  stroke={getGaugeColor(creditScore)}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  initial={{ strokeDashoffset: circumference }}
                  animate={{ strokeDashoffset }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                />
              </svg>
              <div className="absolute inset-x-0 bottom-0 flex flex-col items-center">
                <span className="text-5xl font-bold text-foreground">{displayScore.toLocaleString()}</span>
                <span className="text-sm text-muted-foreground mt-1">{t("score.outOf", { defaultValue: "out of" })} {CBS_CONSTANTS.MAX_CREDIT_SCORE.toLocaleString()}</span>
                {(() => {
                  let grade: string, gradeColor: string;
                  if (creditScore >= 8500) { grade = "A+"; gradeColor = "bg-success text-success-foreground"; }
                  else if (creditScore >= 7000) { grade = "A"; gradeColor = "bg-success text-success-foreground"; }
                  else if (creditScore >= 5500) { grade = "B+"; gradeColor = "bg-info text-info-foreground"; }
                  else if (creditScore >= 4000) { grade = "B"; gradeColor = "bg-info text-info-foreground"; }
                  else if (creditScore >= 2500) { grade = "C"; gradeColor = "bg-warning text-warning-foreground"; }
                  else { grade = "D"; gradeColor = "bg-destructive text-destructive-foreground"; }
                  return (
                    <span className={cn("mt-1 px-4 py-1 rounded-full text-sm font-bold", gradeColor)}>
                      {grade}
                    </span>
                  );
                })()}
              </div>
            </div>

          </motion.div>

          {/* Stat Cards Row */}
          <div className="flex justify-center">
            <Card
              interactive
              className="p-4 text-center min-w-[140px]"
              onClick={() => navigate("/batch/history")}
            >
              <span className="text-2xl">🐔</span>
              <p className="text-lg font-bold text-foreground mt-1">
                {batchCount}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("cbs.score.pastBatches", { defaultValue: "Past Batches" })}
              </p>
            </Card>
          </div>


          {/* 10 Poultry Factors */}
          <div className="space-y-2.5">
            <h3 className="text-sm font-semibold text-foreground">
              {t("cbs.score.factors", { defaultValue: "Score Factors" })}
            </h3>

            {poultryFactors.map((factor, index) => {
              const score = getFactorScore(factor.scoreKey);
              const trend = deriveTrend(score);
              const Icon = factor.icon;
              const isExpanded = expandedFactor === factor.id;

              return (
                <motion.div
                  key={factor.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.25, delay: index * 0.04 }}
                >
                  <Card className={cn("overflow-hidden transition-all", isExpanded && "ring-2 ring-primary")}>
                    <button
                      onClick={() => {
                        if ("vibrate" in navigator) navigator.vibrate(10);
                        setExpandedFactor(isExpanded ? null : factor.id);
                      }}
                      className="w-full flex items-center gap-3 p-3"
                    >
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-muted">
                        <Icon className="w-4 h-4 text-foreground" />
                      </div>

                      <div className="flex-1 min-w-0">
                        {!isSimpleMode && (
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-muted-foreground truncate">
                              {t(factor.labelKey, { defaultValue: factor.label })}
                            </span>
                            <span className="text-xs text-muted-foreground ml-1 shrink-0">
                              {factor.weight}%
                            </span>
                          </div>
                        )}
                        <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${score}%` }}
                            transition={{ duration: 0.8, delay: index * 0.04 }}
                            className={cn("h-full rounded-full", factorBarColor(score))}
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        {!isSimpleMode && (
                          <span className="text-sm font-bold text-foreground w-8 text-right">{score}</span>
                        )}
                        {trendIcon(trend)}
                      </div>

                      <ChevronDown
                        className={cn("w-4 h-4 text-muted-foreground transition-transform shrink-0", isExpanded && "rotate-180")}
                      />
                    </button>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <button
                            onClick={() => {
                              if ("vibrate" in navigator) navigator.vibrate(15);
                              navigate(factor.quickWin.route);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 bg-muted/50 border-t border-border"
                          >
                            <Icon className="w-5 h-5 text-primary" />
                            {!isSimpleMode && (
                              <span className="flex-1 text-sm text-foreground text-left">
                                {t(factor.quickWin.actionKey, { defaultValue: factor.quickWin.action })}
                              </span>
                            )}
                            <span className="text-sm font-bold text-primary">+{factor.quickWin.points}</span>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Improve Score Action Card — only show when points > 0 */}
          {lowestFactor.quickWin.points > 0 && getFactorScore(lowestFactor.scoreKey) < 100 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.5 }}
            >
              <Card
                elevation="high"
                interactive
                className="p-5 gradient-primary"
                onClick={() => {
                  if ("vibrate" in navigator) navigator.vibrate(15);
                  navigate(lowestFactor.quickWin.route);
                }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                    <lowestFactor.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    {!isSimpleMode && (
                      <p className="text-sm text-white/80 mb-1">
                        {t("cbs.score.improveScore", "Improve Your Score")}
                      </p>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-white">+{lowestFactor.quickWin.points}</span>
                      {!isSimpleMode && <span className="text-sm text-white/80">{t("common.points", "points")}</span>}
                    </div>
                  </div>
                  <ChevronRight className="w-6 h-6 text-white shrink-0" />
                </div>
              </Card>
            </motion.div>
          )}

          {/* My Money Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.6 }}
          >
            <Card
              elevation="medium"
              interactive
              className="p-5"
              onClick={() => {
                if ("vibrate" in navigator) navigator.vibrate(15);
                navigate("/money");
              }}
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-success/20 flex items-center justify-center shrink-0">
                  <CurrencyIcon size={28} className="text-success" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-semibold text-foreground simple-hide">
                    {t("money.myMoney", "My Money")}
                  </p>
                </div>
                <ChevronRight className="w-6 h-6 text-muted-foreground shrink-0" />
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </Screen>
  );
};
